import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Placeholder from "@tiptap/extension-placeholder";
import FontFamily from "@tiptap/extension-font-family";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { CollapsibleSection, DrawingNode, FontSize, TableKeymap } from '@/lib/tiptapExtensions';
import { uploadNoteImage } from "@/lib/note-image-upload";
import InsertTableDialog from "./insert-table-dialog";
import {
  Save,
  Trash2,
  X,
  Tag,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link2,
  ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  Pin,
  PinOff,
  LinkIcon,
  Target,
  CheckSquare,
  RefreshCw,
  XCircle,
  Sparkles,
  UserPlus,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronsUp,
  ChevronsDown,
  PenTool,
  Send,
  MessageCircle,
  Bot,
  User,
  FileText,
  RotateCcw,
  Star,
  Check,
  Paperclip,
  ExternalLink,
  HardDrive,
  FolderOpen,
  Table as TableIcon,
  Rocket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest, queryClient as importedQueryClient } from "@/lib/queryClient";
import LinkEntityDialog from "./link-entity-dialog";
import MediaUploadDialog from "./media-upload-dialog";
import ShareDialog from "./share-dialog";
import DocumentUploadButton from "./document-upload-button";
import CreatePlanDialog from "./create-plan-dialog";
import { InlineMediaPreview, mediaKindFromMetadata } from "@/components/attachments/inline-media";

/**
 * Inline preview for a note's uploaded video/image attachment. The media list
 * endpoint strips the heavy fileUrl, so the content is fetched on demand from
 * the authorized per-file content endpoint.
 */
function NoteMediaAttachmentPreview({ attachmentId, mediaId, name, fileType }: {
  attachmentId: string;
  mediaId: string;
  name: string;
  fileType?: string | null;
}) {
  const { data } = useQuery<{ fileUrl: string }>({
    queryKey: [`/api/media/${mediaId}/content`],
  });
  if (!data?.fileUrl) return null;
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="px-3 py-2 border-b bg-muted/30 text-xs font-medium truncate">{name}</div>
      <InlineMediaPreview file={{ name, fileType, fileUrl: data.fileUrl }} testId={`attachment-${attachmentId}`} />
    </div>
  );
}
import type { Note, NoteLink, Goal, Habit, Task, UserPreferences, NoteMedia, NoteDriveFile, NoteUrlAttachment, MediaFile, DriveFile } from "@shared/schema";

interface NoteEditorProps {
  note: Note | null;
  isCreating: boolean;
  isSaving?: boolean;
  onSave: (data: { title: string; content: any; plainContent: string; tags: string[]; type?: string; researchQuestion?: string }) => void;
  onDelete?: () => void;
  onCancel: () => void;
}

export default function NoteEditor({ note, isCreating, isSaving = false, onSave, onDelete, onCancel }: NoteEditorProps) {
  const [title, setTitle] = useState(note?.title || "");
  const [tags, setTags] = useState<string[]>(note?.tags || []);
  const [newTag, setNewTag] = useState("");
  const [noteType, setNoteType] = useState<"note" | "research">(note?.type === "research" ? "research" : "note");
  const [researchQuestion, setResearchQuestion] = useState(note?.researchQuestion || "");
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showMediaDialog, setShowMediaDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [hasCollapsibleSections, setHasCollapsibleSections] = useState(false);
  
  // AI Chatbot state
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string; markedAsResult?: boolean; messageNumber?: number }>>([]);
  const chatMessagesRef = useRef<Array<{ role: 'user' | 'assistant'; content: string; markedAsResult?: boolean; messageNumber?: number }>>([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatComplete, setIsChatComplete] = useState(false);
  
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [lastEdited, setLastEdited] = useState(0);
  const ignoreNextUpdate = useRef(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: noteLinks = [] } = useQuery<NoteLink[]>({
    queryKey: ["/api/notes", note?.id, "links"],
    enabled: !!note?.id && !isCreating,
  });

  const { data: goals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: habits = [] } = useQuery<Habit[]>({
    queryKey: ["/api/habits"],
  });

  const { data: tasks = [] } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
  });

  // Fetch note attachments
  const { data: noteMediaAttachments = [] } = useQuery<NoteMedia[]>({
    queryKey: ["/api/notes", note?.id, "media"],
    enabled: !!note?.id && !isCreating,
  });

  const { data: noteDriveAttachments = [] } = useQuery<NoteDriveFile[]>({
    queryKey: ["/api/notes", note?.id, "drive"],
    enabled: !!note?.id && !isCreating,
  });

  const { data: noteUrlAttachments = [] } = useQuery<NoteUrlAttachment[]>({
    queryKey: ["/api/notes", note?.id, "url"],
    enabled: !!note?.id && !isCreating,
  });

  // Fetch media files to display attachment details
  const { data: allMediaFiles = [] } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
    enabled: noteMediaAttachments.length > 0,
  });

  // Fetch drive files to display attachment details
  const { data: allDriveFiles = [] } = useQuery<DriveFile[]>({
    queryKey: ["/api/google-drive/files"],
    enabled: noteDriveAttachments.length > 0,
  });

  // User preferences for metadata collapsed state
  const { data: userPreferences } = useQuery<UserPreferences>({
    queryKey: ["/api/user/preferences"],
  });

  const [metadataCollapsed, setMetadataCollapsed] = useState(false);

  // Sync local state with database when preferences load
  useEffect(() => {
    if (userPreferences?.notepadMetadataCollapsed === true || userPreferences?.notepadMetadataCollapsed === false) {
      setMetadataCollapsed(userPreferences.notepadMetadataCollapsed);
    }
  }, [userPreferences?.notepadMetadataCollapsed]);

  // Keep ref in sync with chatMessages state
  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  // Load persisted research chat history when a note is opened
  useEffect(() => {
    const saved = (note as any)?.researchChatHistory;
    if (Array.isArray(saved) && saved.length > 0) {
      setChatMessages(saved);
      chatMessagesRef.current = saved;
    } else {
      setChatMessages([]);
      chatMessagesRef.current = [];
    }
  }, [note?.id]);

  const updatePreferencesMutation = useMutation({
    mutationFn: async (collapsed: boolean) => {
      const response = await fetch('/api/user/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ notepadMetadataCollapsed: collapsed }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to update preferences');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    },
  });

  const toggleMetadataCollapsed = () => {
    const newState = !metadataCollapsed;
    setMetadataCollapsed(newState);
    updatePreferencesMutation.mutate(newState);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Highlight.configure({
        multicolor: true,
      }),
      Subscript,
      Superscript,
      Placeholder.configure({
        placeholder: "Start writing your note...",
      }),
      CollapsibleSection,
      DrawingNode,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TableKeymap,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none min-h-[300px] p-4 !max-w-none w-full',
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith('image/'));
        if (imageItem) {
          const file = imageItem.getAsFile();
          if (file) {
            event.preventDefault();
            // Upload to object storage (no base64 in the note/database), then
            // insert the served URL at the current selection.
            void uploadNoteImage(file)
              .then((src) => {
                const { schema } = view.state;
                const node = schema.nodes.image.create({ src });
                view.dispatch(view.state.tr.replaceSelectionWith(node));
              })
              .catch((error: any) => {
                toast({
                  title: "Image Upload Failed",
                  description: error?.message || "Could not upload pasted image",
                  variant: "destructive",
                });
              });
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find((file) => file.type.startsWith('image/'));
        if (imageFile) {
          event.preventDefault();
          const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
          // Upload to object storage (no base64 in the note/database), then
          // insert the served URL at the drop position.
          void uploadNoteImage(imageFile)
            .then((src) => {
              const { schema } = view.state;
              const node = schema.nodes.image.create({ src });
              view.dispatch(
                view.state.tr.insert(coordinates?.pos ?? view.state.selection.from, node),
              );
            })
            .catch((error: any) => {
              toast({
                title: "Image Upload Failed",
                description: error?.message || "Could not upload dropped image",
                variant: "destructive",
              });
            });
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      if (ignoreNextUpdate.current) {
        ignoreNextUpdate.current = false;
      } else {
        setLastEdited(Date.now());
      }
      let hasCollapsibles = false;
      updatedEditor.state.doc.descendants((node: any) => {
        if (node.type.name === 'collapsibleSection') {
          hasCollapsibles = true;
          return false;
        }
        return undefined;
      });
      setHasCollapsibleSections(hasCollapsibles);
    },
    onCreate: ({ editor: createdEditor }) => {
      let hasCollapsibles = false;
      createdEditor.state.doc.descendants((node: any) => {
        if (node.type.name === 'collapsibleSection') {
          hasCollapsibles = true;
          return false;
        }
        return undefined;
      });
      setHasCollapsibleSections(hasCollapsibles);
    },
  });

  useEffect(() => {
    if (note && editor) {
      setLastEdited(0);
      setAutoSaveStatus('saved');
      setTitle(note.title);
      setTags(note.tags);
      setNoteType(note.type === "research" ? "research" : "note");
      setResearchQuestion(note.researchQuestion || "");
      
      // Handle content - validate it's a proper Tiptap document before setting
      let contentLoaded = false;
      
      if (note.content) {
        let parsedContent: any = note.content;
        
        // If content is a string, try to parse it as JSON
        if (typeof note.content === 'string') {
          try {
            parsedContent = JSON.parse(note.content);
          } catch (e) {
            // Not valid JSON, will fall through to plainContent
            parsedContent = null;
          }
        }
        
        // Validate it's a proper Tiptap document with type: 'doc' and content array
        if (parsedContent && 
            typeof parsedContent === 'object' && 
            parsedContent.type === 'doc' && 
            Array.isArray(parsedContent.content) && 
            parsedContent.content.length > 0) {
          ignoreNextUpdate.current = true;
          editor.commands.setContent(parsedContent);
          contentLoaded = true;
        }
      }
      
      // Fallback to plain content if rich content is invalid/empty
      if (!contentLoaded && note.plainContent) {
        ignoreNextUpdate.current = true;
        editor.commands.setContent(note.plainContent);
        contentLoaded = true;
      }
      
      // Default to empty if no content available
      if (!contentLoaded) {
        ignoreNextUpdate.current = true;
        editor.commands.setContent("");
      }
    } else if (isCreating && editor) {
      // Reset for new note creation
      setLastEdited(0);
      setAutoSaveStatus('saved');
      setTitle("");
      setTags([]);
      setNoteType("note");
      setResearchQuestion("");
      ignoreNextUpdate.current = true;
      editor.commands.setContent("");
    }
  }, [note, editor, isCreating]);

  // Auto-save for existing notes (triggered by editor content or title/tag changes)
  useEffect(() => {
    if (isCreating || !note?.id || !editor) return;
    const hasChanges = lastEdited > 0 || title !== note.title || JSON.stringify(tags) !== JSON.stringify(note.tags || []);
    if (!hasChanges) return;
    setAutoSaveStatus('unsaved');
    const timeoutId = setTimeout(async () => {
      setAutoSaveStatus('saving');
      try {
        const content = editor.getJSON();
        const plainContent = editor.getText();
        const res = await fetch(`/api/notes/${note.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title, content, plainContent, tags }),
        });
        if (!res.ok) throw new Error('Save failed');
        queryClient.invalidateQueries({ queryKey: ['/api/notes'] });
        queryClient.invalidateQueries({ queryKey: ['/api/notes/tags'] });
        setAutoSaveStatus('saved');
      } catch {
        setAutoSaveStatus('unsaved');
      }
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [lastEdited, title, tags]);

  // Auto-create for new notes (triggered once user starts typing with a title)
  useEffect(() => {
    if (!isCreating || !editor || !title.trim() || lastEdited === 0) return;
    const timeoutId = setTimeout(() => {
      const content = editor.getJSON();
      const plainContent = editor.getText();
      const finalTags = [...tags];
      if (newTag.trim() && !finalTags.includes(newTag.trim())) finalTags.push(newTag.trim());
      if (noteType === "research") {
        if (!finalTags.includes("research")) finalTags.push("research");
        if (!finalTags.includes("ai-generated")) finalTags.push("ai-generated");
      }
      const firstUserMessage = chatMessages.find(m => m.role === 'user')?.content || '';
      const effectiveResearchQuestion = firstUserMessage || researchQuestion.trim();
      onSave({
        title: title.trim(),
        content,
        plainContent,
        tags: finalTags,
        type: noteType,
        researchQuestion: noteType === "research" ? effectiveResearchQuestion : undefined,
      });
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [lastEdited, title, isCreating]);

  const pinMutation = useMutation({
    mutationFn: async () => {
      if (!note?.id) return;
      const response = await fetch(`/api/notes/${note.id}/pin`, {
        method: note.isPinned ? 'DELETE' : 'POST',
      });
      if (!response.ok) throw new Error('Failed to toggle pin');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes"] });
    },
    onError: () => {
    },
  });

  const saveNoteLinks = useMutation({
    mutationFn: async (links: { entityType: string; entityId: string }[]) => {
      if (!note?.id) return;
      
      const existingLinkKeys = new Set(
        noteLinks.map(link => `${link.entityType}:${link.entityId}`)
      );
      const newLinkKeys = new Set(
        links.map(link => `${link.entityType}:${link.entityId}`)
      );

      const linksToAdd = links.filter(
        link => !existingLinkKeys.has(`${link.entityType}:${link.entityId}`)
      );
      const linksToRemove = noteLinks.filter(
        link => !newLinkKeys.has(`${link.entityType}:${link.entityId}`)
      );

      for (const link of linksToAdd) {
        const response = await fetch(`/api/notes/${note.id}/links`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(link),
        });
        if (!response.ok) throw new Error('Failed to create link');
      }

      for (const link of linksToRemove) {
        const response = await fetch(`/api/notes/links/${link.id}`, {
          method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete link');
      }
    },
    onSuccess: (_, links) => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "links"] });
      // Also invalidate any entity's linked notes lists
      for (const link of links) {
        queryClient.invalidateQueries({ queryKey: ["/api", link.entityType, link.entityId, "notes"] });
      }
      // Invalidate any goals/habits/tasks linked notes
      queryClient.invalidateQueries({ queryKey: ["/api/notes/links"] });
      setShowLinkDialog(false);
    },
    onError: (error) => {
      console.error("Error saving note links:", error);
    },
  });

  const removeLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      const response = await fetch(`/api/notes/links/${linkId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete link');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "links"] });
    },
    onError: () => {
    },
  });

  const saveResearchHistoryMutation = useMutation({
    mutationFn: async (history: Array<{ role: 'user' | 'assistant'; content: string; markedAsResult?: boolean; messageNumber?: number }>) => {
      if (!note?.id) return;
      const response = await fetch(`/api/notes/${note.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ researchChatHistory: history }),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to save research history');
      return response.json();
    },
  });

  const generateResearchMutation = useMutation({
    mutationFn: async ({ question, conversationHistory }: { question: string; conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> }) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      
      try {
        const response = await fetch('/api/notes/research', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, conversationHistory }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Failed to generate research');
        return response.json();
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Research is taking longer than expected. Please try again.');
        }
        throw error;
      }
    },
    onSuccess: (data: { answer: string }) => {
      if (!data.answer || data.answer.trim() === "") {
        return;
      }
      const prev = chatMessagesRef.current;
      const assistantCount = prev.filter(m => m.role === 'assistant').length + 1;
      const updated = [...prev, { 
        role: 'assistant' as const, 
        content: data.answer,
        messageNumber: assistantCount,
        markedAsResult: false
      }];
      setChatMessages(updated);
      chatMessagesRef.current = updated;
      if (note?.id) {
        saveResearchHistoryMutation.mutate(updated);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Research Failed",
        description: error?.message || "Failed to generate research. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    const userMsgCount = chatMessages.filter(m => m.role === 'user').length + 1;
    const updatedMessages = [...chatMessages, { 
      role: 'user' as const, 
      content: userMessage,
      messageNumber: userMsgCount 
    }];
    setChatMessages(updatedMessages);
    setChatInput("");
    setIsChatComplete(false);
    // Send full conversation history for context (excluding current question which is sent separately)
    // The backend adds the current question to the end, so we send the full history up to this point
    generateResearchMutation.mutate({ 
      question: userMessage, 
      conversationHistory: updatedMessages.slice(0, -1) // All messages except the one we just added
    });
  };

  const toggleMarkAsResult = (index: number) => {
    setChatMessages(prev => prev.map((msg, i) => 
      i === index ? { ...msg, markedAsResult: !msg.markedAsResult } : msg
    ));
  };

  const markedResults = chatMessages.filter(m => m.markedAsResult);

  // Delete attachment mutations
  const deleteMediaAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await fetch(`/api/notes/${note?.id}/media/${attachmentId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete attachment');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "media"] });
      toast({ title: "Attachment removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove attachment", variant: "destructive" });
    },
  });

  const deleteDriveAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await fetch(`/api/notes/${note?.id}/drive/${attachmentId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete attachment');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "drive"] });
      toast({ title: "Attachment removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove attachment", variant: "destructive" });
    },
  });

  const deleteUrlAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await fetch(`/api/notes/${note?.id}/url/${attachmentId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete attachment');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "url"] });
      toast({ title: "Attachment removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove attachment", variant: "destructive" });
    },
  });

  const handleAttachmentAdded = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "media"] });
    queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "drive"] });
    queryClient.invalidateQueries({ queryKey: ["/api/notes", note?.id, "url"] });
  };

  // Helper function to get media file details
  const getMediaFileDetails = (mediaId: string) => {
    return allMediaFiles.find(f => f.id === mediaId);
  };

  // Helper function to get drive file details
  const getDriveFileDetails = (driveFileId: string) => {
    return allDriveFiles.find(f => f.id === driveFileId);
  };

  const totalAttachments = noteMediaAttachments.length + noteDriveAttachments.length + noteUrlAttachments.length;

  const handlePostPlanToNote = (planText: string) => {
    if (!editor) return;
    const existingContent = editor.getJSON();
    const existingNodes = existingContent.content || [];
    const hasExistingContent = existingNodes.some((node: any) => {
      if (node.type === 'paragraph' && node.content) {
        return node.content.some((child: any) => child.text && child.text.trim());
      }
      return false;
    });
    const newContent: any[] = [];
    if (hasExistingContent) {
      newContent.push({ type: 'paragraph', content: [] });
    }
    for (const line of planText.split('\n')) {
      newContent.push({
        type: 'paragraph',
        content: line.trim() === '' ? [] : [{ type: 'text', text: line }],
      });
    }
    editor.commands.setContent({
      type: 'doc',
      content: [...existingNodes, ...newContent],
    });
  };

  const handlePostChatToNote = (resultsOnly: boolean = false) => {
    if (!editor || chatMessages.length === 0) return;
    
    // Get messages to post
    const messagesToPost = resultsOnly 
      ? chatMessages.filter(m => m.markedAsResult)
      : chatMessages;
    
    if (messagesToPost.length === 0) {
      toast({
        title: "No content to post",
        description: resultsOnly 
          ? "Please mark at least one response as a result first."
          : "No messages to post.",
        variant: "destructive",
      });
      return;
    }
    
    // Format chat conversation as rich content - append to existing content
    const existingContent = editor.getJSON();
    const existingNodes = existingContent.content || [];
    
    // Check if there's meaningful existing content
    const hasExistingContent = existingNodes.some((node: any) => {
      if (node.type === 'paragraph' && node.content) {
        return node.content.some((child: any) => child.text && child.text.trim());
      }
      return false;
    });

    // Check if there's already a separator to avoid duplicates
    const hasSeparator = existingNodes.some((node: any) => {
      if (node.type === 'paragraph' && node.content) {
        return node.content.some((child: any) => 
          child.text && (child.text.includes('--- AI Research Results ---') || child.text.includes('--- AI Research Conversation ---'))
        );
      }
      return false;
    });

    const newContent: any[] = [];
    const separatorText = resultsOnly ? '--- AI Research Results ---' : '--- AI Research Conversation ---';
    
    // Add separator if there's existing content and no separator yet
    if (hasExistingContent && !hasSeparator) {
      newContent.push({ type: 'paragraph', content: [] });
      newContent.push({
        type: 'paragraph',
        content: [{
          type: 'text',
          marks: [{ type: 'bold' }],
          text: separatorText
        }]
      });
      newContent.push({ type: 'paragraph', content: [] });
    } else if (hasExistingContent && hasSeparator) {
      // Add a smaller separator for additional posts
      newContent.push({ type: 'paragraph', content: [] });
      newContent.push({
        type: 'paragraph',
        content: [{
          type: 'text',
          marks: [{ type: 'italic' }],
          text: resultsOnly ? '(additional results)' : '(continued conversation)'
        }]
      });
      newContent.push({ type: 'paragraph', content: [] });
    }
    
    messagesToPost.forEach((msg) => {
      // Add role header
      newContent.push({
        type: 'paragraph',
        content: [{
          type: 'text',
          marks: [{ type: 'bold' }],
          text: msg.role === 'user' ? 'You: ' : 'AI Assistant: '
        }]
      });
      
      // Add message content - split by paragraphs
      const paragraphs = msg.content.split('\n\n').filter(p => p.trim());
      paragraphs.forEach(paragraph => {
        newContent.push({
          type: 'paragraph',
          content: [{ type: 'text', text: paragraph.trim() }]
        });
      });
      
      // Add separator
      newContent.push({ type: 'paragraph', content: [] });
    });
    
    // Append new content to existing content
    const combinedContent = { 
      type: 'doc', 
      content: hasExistingContent ? [...existingNodes, ...newContent] : newContent 
    };
    
    editor.chain().focus().setContent(combinedContent).run();
    setIsChatComplete(true);
    
    const itemType = resultsOnly ? "results" : "conversation";
    toast({
      title: "Posted to Note",
      description: hasExistingContent 
        ? `AI ${itemType} has been appended to your note.`
        : `AI ${itemType} has been added to your note.`,
    });
  };

  const handleClearChat = () => {
    setChatMessages([]);
    chatMessagesRef.current = [];
    if (note?.id) {
      saveResearchHistoryMutation.mutate([]);
    }
    setChatInput("");
    setIsChatComplete(false);
  };

  const handleSave = () => {
    if (!title.trim()) {
      return;
    }

    // For research notes, require either chat messages or a research question
    const firstUserMessage = chatMessages.find(m => m.role === 'user')?.content || '';
    const effectiveResearchQuestion = firstUserMessage || researchQuestion.trim();
    
    if (noteType === "research" && !effectiveResearchQuestion) {
      toast({
        title: "Research question required",
        description: "Please ask at least one question in the chat before saving.",
        variant: "destructive",
      });
      return;
    }

    if (!editor) return;

    const content = editor.getJSON();
    const plainContent = editor.getText();

    // Add any pending tag from the input field before saving
    let finalTags = [...tags];
    if (newTag.trim() && !finalTags.includes(newTag.trim())) {
      finalTags.push(newTag.trim());
    }

    // Automatically add research tags for research notes
    if (noteType === "research") {
      if (!finalTags.includes("research")) {
        finalTags.push("research");
      }
      if (!finalTags.includes("ai-generated")) {
        finalTags.push("ai-generated");
      }
    }

    onSave({
      title: title.trim(),
      content,
      plainContent,
      tags: finalTags,
      type: noteType,
      researchQuestion: noteType === "research" ? effectiveResearchQuestion : undefined,
    });
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const setLink = () => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImage = () => {
    setShowMediaDialog(true);
  };

  const handleInsertTable = (rows: number, cols: number) => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
  };

  const handleMediaSelected = (url: string) => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const getLinkedEntity = (link: NoteLink): { name: string; icon: any; color: string } | null => {
    if (link.entityType === 'goal') {
      const goal = goals.find(g => g.id === link.entityId);
      return goal ? { name: goal.title, icon: Target, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' } : null;
    } else if (link.entityType === 'habit') {
      const habit = habits.find(h => h.id === link.entityId);
      return habit ? { name: habit.name, icon: RefreshCw, color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' } : null;
    } else if (link.entityType === 'task') {
      const task = tasks.find(t => t.id === link.entityId);
      return task ? { name: task.title, icon: CheckSquare, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' } : null;
    }
    return null;
  };

  if (!editor) return null;

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Fixed Header with Action Buttons */}
      <div className="flex-shrink-0 flex items-center justify-between pb-4">
        <h2 className="text-2xl font-bold">
          {isCreating ? "Create New Note" : "Edit Note"}
        </h2>
        <div className="flex items-center gap-2">
          {!isCreating && note && (
            <>
              <span className="text-sm text-muted-foreground">
                {autoSaveStatus === 'saving' && '💾 Saving...'}
                {autoSaveStatus === 'saved' && '✓ Saved'}
                {autoSaveStatus === 'unsaved' && '⚠ Unsaved'}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => pinMutation.mutate()}
                disabled={pinMutation.isPending}
                data-testid="button-pin-note"
              >
                {note.isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLinkDialog(true)}
                data-testid="button-link-entity-header"
                className="h-8 px-2.5 text-xs"
              >
                <LinkIcon className="h-3.5 w-3.5 mr-1.5" />
                Link
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            data-testid="button-cancel-note"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Collapsible Metadata Section */}
      <div className="flex-shrink-0 border rounded-lg bg-primary/5 mb-4">
        <div 
          className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-primary/10 transition-colors"
          onClick={toggleMetadataCollapsed}
          data-testid="button-toggle-metadata"
        >
          <span className="text-sm font-medium text-muted-foreground">
            {metadataCollapsed ? 'Show Details' : 'Note Details'}
          </span>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {metadataCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        {!metadataCollapsed && (
          <div className="px-4 pb-4 space-y-4">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Note title..."
              className="text-lg font-semibold"
              data-testid="input-note-title"
            />

            {/* Type Toggle and Action Buttons Row */}
            <div className="flex items-center justify-between gap-3 pt-3 border-t flex-wrap">
              <div className="flex items-center gap-3">
                <Label htmlFor="note-type-toggle" className="text-sm font-medium">
                  Note Type:
                </Label>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${noteType === "note" ? "font-medium" : "text-muted-foreground"}`}>
                    Note
                  </span>
                  <Switch
                    id="note-type-toggle"
                    checked={noteType === "research"}
                    onCheckedChange={(checked) => setNoteType(checked ? "research" : "note")}
                    data-testid="switch-note-type"
                  />
                  <span className={`text-sm ${noteType === "research" ? "font-medium" : "text-muted-foreground"}`}>
                    Research
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-1.5">
                {!isCreating && note && (
                  <>
                    <DocumentUploadButton
                      entityType="note"
                      entityId={note.id}
                      onAttachmentAdded={handleAttachmentAdded}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowShareDialog(true)}
                      data-testid="button-share-note"
                      className="h-8 px-2.5 text-xs"
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                      Share
                    </Button>
                  </>
                )}
                {!isCreating && onDelete && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={onDelete}
                    data-testid="button-delete-note"
                    className="h-8 px-2.5 text-xs"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Delete
                  </Button>
                )}
              </div>
            </div>

            {/* AI Research Chatbot */}
            {noteType === "research" && (
              <div className="border-2 border-blue-400 rounded-lg p-3 bg-blue-50/50 dark:bg-blue-950/20 space-y-3">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <MessageCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">AI Research Assistant</span>
                  {chatMessages.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearChat}
                      className="ml-auto h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Chat Messages */}
                {chatMessages.length > 0 && (
                  <div className="max-h-[350px] overflow-y-auto space-y-3 p-2 bg-white dark:bg-background rounded-md border">
                    {chatMessages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="flex flex-col items-center gap-1">
                            <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                              msg.markedAsResult 
                                ? 'bg-green-100 dark:bg-green-900 ring-2 ring-green-500' 
                                : 'bg-blue-100 dark:bg-blue-900'
                            }`}>
                              <Bot className={`h-4 w-4 ${
                                msg.markedAsResult 
                                  ? 'text-green-600 dark:text-green-400' 
                                  : 'text-blue-600 dark:text-blue-400'
                              }`} />
                            </div>
                            {msg.messageNumber && (
                              <span className="text-[10px] text-muted-foreground font-mono">#{msg.messageNumber}</span>
                            )}
                          </div>
                        )}
                        <div className="flex flex-col gap-1 max-w-[80%]">
                          <div
                            className={`rounded-lg p-3 text-sm ${
                              msg.role === 'user'
                                ? 'bg-primary text-primary-foreground'
                                : msg.markedAsResult
                                  ? 'bg-green-50 dark:bg-green-950 border-2 border-green-400'
                                  : 'bg-muted'
                            }`}
                          >
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                          </div>
                          {msg.role === 'assistant' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleMarkAsResult(index)}
                              className={`self-start h-6 px-2 text-xs ${
                                msg.markedAsResult 
                                  ? 'text-green-600 hover:text-green-700 bg-green-50 dark:bg-green-950' 
                                  : 'text-muted-foreground hover:text-foreground'
                              }`}
                            >
                              {msg.markedAsResult ? (
                                <>
                                  <Check className="h-3 w-3 mr-1" />
                                  Marked as Result
                                </>
                              ) : (
                                <>
                                  <Star className="h-3 w-3 mr-1" />
                                  Mark as Result
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        {msg.role === 'user' && (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            {msg.messageNumber && (
                              <span className="text-[10px] text-muted-foreground font-mono">Q{msg.messageNumber}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {generateResearchMutation.isPending && (
                      <div className="flex gap-2 justify-start">
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="bg-muted rounded-lg p-3 text-sm">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
                            <span className="text-muted-foreground">Thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Chat Input */}
                <div className="flex gap-2">
                  <Textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder={chatMessages.length === 0 
                      ? "Ask your research question... (e.g., 'What are the benefits of meditation?')"
                      : "Ask a follow-up question..."
                    }
                    className="text-sm min-h-[50px] resize-none overflow-hidden flex-1"
                    style={{ height: 'auto' }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = 'auto';
                      target.style.height = `${Math.max(50, target.scrollHeight)}px`;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendChatMessage();
                      }
                    }}
                    data-testid="input-chat-message"
                  />
                  <Button
                    onClick={handleSendChatMessage}
                    disabled={generateResearchMutation.isPending || !chatInput.trim()}
                    size="icon"
                    className="h-[50px] w-[50px] shrink-0"
                    data-testid="button-send-chat"
                  >
                    {generateResearchMutation.isPending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Post Results Section - Shows when chat has messages */}
                {chatMessages.length > 0 && chatMessages.some(m => m.role === 'assistant') && (
                  <div className="border-2 border-red-400 rounded-lg p-3 bg-red-50/50 dark:bg-red-950/20 space-y-3">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm font-medium">Save to Note</span>
                      {markedResults.length > 0 && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                          {markedResults.length} result{markedResults.length !== 1 ? 's' : ''} marked
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      {/* Post Results Only Button */}
                      <Button
                        onClick={() => handlePostChatToNote(true)}
                        size="sm"
                        disabled={markedResults.length === 0}
                        className={markedResults.length > 0 
                          ? "bg-green-600 hover:bg-green-700 text-white flex-1" 
                          : "flex-1"
                        }
                        data-testid="button-post-results"
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Post Results Only ({markedResults.length})
                      </Button>
                      
                      {/* Post Full Conversation Button */}
                      <Button
                        onClick={() => handlePostChatToNote(false)}
                        size="sm"
                        variant="outline"
                        className="flex-1 border-red-400 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                        data-testid="button-post-full-chat"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        Post Full Conversation
                      </Button>

                      {/* Create Plan Button */}
                      <Button
                        onClick={() => setShowCreatePlan(true)}
                        size="sm"
                        className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="button-create-plan"
                      >
                        <Rocket className="h-4 w-4 mr-1" />
                        Create Plan
                      </Button>
                    </div>
                    
                    {markedResults.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Tip: Click "Mark as Result" on any AI response to select specific results to post.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add tag..."
                className="h-6 w-32 text-xs"
                data-testid="input-note-tags"
              />
            </div>

            {/* Attachments Section */}
            {!isCreating && totalAttachments > 0 && (
              <div className="space-y-2 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Attachments ({totalAttachments})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="note-attachments-list">
                  {/* Media Center Attachments */}
                  {noteMediaAttachments.map((attachment) => {
                    const mediaFile = getMediaFileDetails(attachment.mediaId);
                    return (
                      <Badge
                        key={attachment.id}
                        variant="outline"
                        className="gap-1 py-1 px-2 cursor-pointer hover:bg-muted"
                      >
                        <FolderOpen className="h-3 w-3" />
                        <span className="text-xs max-w-[150px] truncate">
                          {mediaFile?.name || 'Media file'}
                        </span>
                        {mediaFile?.fileUrl && (
                          <a
                            href={mediaFile.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        <button
                          onClick={() => deleteMediaAttachmentMutation.mutate(attachment.id)}
                          className="ml-1 hover:text-destructive"
                          disabled={deleteMediaAttachmentMutation.isPending}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}

                  {/* Google Drive Attachments */}
                  {noteDriveAttachments.map((attachment) => {
                    const driveFile = getDriveFileDetails(attachment.driveFileId);
                    return (
                      <Badge
                        key={attachment.id}
                        variant="outline"
                        className="gap-1 py-1 px-2 cursor-pointer hover:bg-muted"
                      >
                        <HardDrive className="h-3 w-3" />
                        <span className="text-xs max-w-[150px] truncate">
                          {driveFile?.name || 'Drive file'}
                        </span>
                        {driveFile?.webViewLink && (
                          <a
                            href={driveFile.webViewLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        <button
                          onClick={() => deleteDriveAttachmentMutation.mutate(attachment.id)}
                          className="ml-1 hover:text-destructive"
                          disabled={deleteDriveAttachmentMutation.isPending}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}

                  {/* URL Attachments */}
                  {noteUrlAttachments.map((attachment) => (
                    <Badge
                      key={attachment.id}
                      variant="outline"
                      className="gap-1 py-1 px-2 cursor-pointer hover:bg-muted"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span className="text-xs max-w-[150px] truncate">
                        {attachment.name}
                      </span>
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        onClick={() => deleteUrlAttachmentMutation.mutate(attachment.id)}
                        className="ml-1 hover:text-destructive"
                        disabled={deleteUrlAttachmentMutation.isPending}
                        data-testid={`button-delete-attachment-${attachment.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                {/* Inline previews for uploaded videos and images */}
                {noteMediaAttachments.map((attachment) => {
                  const mediaFile = getMediaFileDetails(attachment.mediaId);
                  if (!mediaFile || !mediaKindFromMetadata(mediaFile)) return null;
                  return (
                    <NoteMediaAttachmentPreview
                      key={`preview-${attachment.id}`}
                      attachmentId={attachment.id}
                      mediaId={attachment.mediaId}
                      name={mediaFile.name}
                      fileType={mediaFile.fileType}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toolbar - Always visible */}
      <div className="flex-shrink-0 mb-4">

        <div className="px-6 py-2 flex flex-wrap gap-1 border-b bg-muted/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'bg-accent' : ''}
            title="Bold"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'bg-accent' : ''}
            title="Italic"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'bg-accent' : ''}
            title="Underline"
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive('strike') ? 'bg-accent' : ''}
            title="Strikethrough"
          >
            <Strikethrough className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-1 h-6" />

          {/* Font Family */}
          <Select
            value={editor.getAttributes('textStyle').fontFamily || 'default'}
            onValueChange={(value) => {
              if (value === 'default') {
                editor.chain().focus().unsetFontFamily().run();
              } else {
                editor.chain().focus().setFontFamily(value).run();
              }
            }}
          >
            <SelectTrigger className="w-[100px] h-7 text-xs" title="Font Family" data-testid="select-font-family">
              <SelectValue placeholder="Font" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="Arial" style={{ fontFamily: 'Arial' }}>Arial</SelectItem>
              <SelectItem value="Georgia" style={{ fontFamily: 'Georgia' }}>Georgia</SelectItem>
              <SelectItem value="Times New Roman" style={{ fontFamily: 'Times New Roman' }}>Times</SelectItem>
              <SelectItem value="Courier New" style={{ fontFamily: 'Courier New' }}>Courier</SelectItem>
              <SelectItem value="Verdana" style={{ fontFamily: 'Verdana' }}>Verdana</SelectItem>
              <SelectItem value="Trebuchet MS" style={{ fontFamily: 'Trebuchet MS' }}>Trebuchet</SelectItem>
              <SelectItem value="Comic Sans MS" style={{ fontFamily: 'Comic Sans MS' }}>Comic Sans</SelectItem>
              <SelectItem value="Impact" style={{ fontFamily: 'Impact' }}>Impact</SelectItem>
            </SelectContent>
          </Select>

          {/* Font Size */}
          <Select
            value={editor.getAttributes('textStyle').fontSize || 'default'}
            onValueChange={(value) => {
              if (value === 'default') {
                (editor.chain().focus() as any).unsetFontSize().run();
              } else {
                (editor.chain().focus() as any).setFontSize(value).run();
              }
            }}
          >
            <SelectTrigger className="w-[65px] h-7 text-xs" title="Font Size" data-testid="select-font-size">
              <SelectValue placeholder="Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="10px">10</SelectItem>
              <SelectItem value="12px">12</SelectItem>
              <SelectItem value="14px">14</SelectItem>
              <SelectItem value="16px">16</SelectItem>
              <SelectItem value="18px">18</SelectItem>
              <SelectItem value="20px">20</SelectItem>
              <SelectItem value="24px">24</SelectItem>
              <SelectItem value="28px">28</SelectItem>
              <SelectItem value="32px">32</SelectItem>
              <SelectItem value="36px">36</SelectItem>
              <SelectItem value="48px">48</SelectItem>
              <SelectItem value="64px">64</SelectItem>
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'bg-accent' : ''}
            title="Heading 1"
          >
            <Heading1 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'bg-accent' : ''}
            title="Heading 2"
          >
            <Heading2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'bg-accent' : ''}
            title="Heading 3"
          >
            <Heading3 className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'bg-accent' : ''}
            title="Bullet List"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'bg-accent' : ''}
            title="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive('blockquote') ? 'bg-accent' : ''}
            title="Quote"
          >
            <Quote className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={editor.isActive('codeBlock') ? 'bg-accent' : ''}
            title="Code Block"
          >
            <Code className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              editor.chain().focus().insertCollapsible().run();
            }}
            className={editor.isActive('collapsibleSection') ? 'bg-accent' : ''}
            title="Insert Collapsible Section"
            data-testid="button-insert-collapsible"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          
          {/* Expand/Collapse All - only show when there are collapsible sections */}
          {hasCollapsibleSections && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().expandAllCollapsibles().run()}
                title="Expand All Sections"
                data-testid="button-expand-all"
              >
                <ChevronsDown className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().collapseAllCollapsibles().run()}
                title="Collapse All Sections"
                data-testid="button-collapse-all"
              >
                <ChevronsUp className="h-4 w-4" />
              </Button>
            </>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              editor.chain().focus().insertDrawing().run();
            }}
            className={editor.isActive('drawingNode') ? 'bg-accent' : ''}
            title="Insert Drawing Canvas"
            data-testid="button-insert-drawing"
          >
            <PenTool className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTableDialog(true)}
            className={editor.isActive('table') ? 'bg-accent' : ''}
            title="Insert Table"
            data-testid="button-insert-table"
          >
            <TableIcon className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Button variant="ghost" size="sm" onClick={setLink} title="Insert Link">
            <Link2 className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={addImage}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full min-h-[44px] min-w-[44px]"
            title="Insert Image"
            data-testid="button-add-media"
          >
            <ImageIcon className="h-5 w-5" />
          </Button>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'bg-accent' : ''}
            title="Align Left"
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'bg-accent' : ''}
            title="Align Center"
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'bg-accent' : ''}
            title="Align Right"
          >
            <AlignRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={editor.isActive({ textAlign: 'justify' }) ? 'bg-accent' : ''}
            title="Justify"
          >
            <AlignJustify className="h-4 w-4" />
          </Button>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className={editor.isActive('highlight') ? 'bg-accent' : ''}
            title="Highlight"
          >
            <Highlighter className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Scrollable Content Area - Editor and Links */}
      <div className="flex-1 w-full overflow-y-auto">
        <Card className="h-full w-full">
          <CardContent className="flex-1 w-full overflow-auto p-0">
            <EditorContent editor={editor} className="h-full w-full tiptap-editor prose prose-sm sm:prose-base dark:prose-invert max-w-none p-4" />
          </CardContent>
        </Card>

        {!isCreating && note && noteLinks.length > 0 && (
          <>
            <Separator />
            <div className="px-6 py-3">
              <div className="flex items-center gap-2 mb-2">
                <LinkIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Linked Entities</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {noteLinks.map((link) => {
                  const entity = getLinkedEntity(link);
                  if (!entity) return null;
                  
                  const Icon = entity.icon;
                  return (
                    <Badge key={link.id} variant="outline" className={`gap-1 ${entity.color}`}>
                      <Icon className="h-3 w-3" />
                      {entity.name}
                      <button
                        onClick={() => removeLinkMutation.mutate(link.id)}
                        className="ml-1 hover:text-destructive"
                        disabled={removeLinkMutation.isPending}
                      >
                        <XCircle className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            </div>
          </>
        )}

      </div>

      {!isCreating && note && (
        <LinkEntityDialog
          open={showLinkDialog}
          onOpenChange={setShowLinkDialog}
          noteId={note.id}
          existingLinks={noteLinks}
          onSave={(links) => saveNoteLinks.mutate(links)}
        />
      )}

      <MediaUploadDialog
        open={showMediaDialog}
        onOpenChange={setShowMediaDialog}
        onMediaSelected={handleMediaSelected}
      />

      <InsertTableDialog
        open={showTableDialog}
        onOpenChange={setShowTableDialog}
        onInsert={handleInsertTable}
      />

      {!isCreating && note && (
        <ShareDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          itemId={note.id}
          itemType="note"
          itemTitle={note.title}
        />
      )}

      <CreatePlanDialog
        open={showCreatePlan}
        onOpenChange={setShowCreatePlan}
        conversationHistory={chatMessages.map(m => ({ role: m.role, content: m.content }))}
        onPostToNotepad={handlePostPlanToNote}
      />
    </div>
  );
}
